// React
import React, {useCallback, useEffect, useRef, useState} from 'react';

//Third party
import {observer} from 'mobx-react';
import 'emoji-mart/css/emoji-mart.css';

// WS
import {Alert, Button, Paper} from '@wellstone-solutions/web';
import {Api} from '@wellstone-solutions/common';
import {Palette} from '../../palette';
import {hiddenIfEmpty} from '../../utils/Styler';
import PfMessage from './PfMessage';
import {getPubnubTimetokenFromTimestamp, scrollTo} from '../../utils/Utils';
import {useStores} from 'hooks/useStores';
import {useIsViewed} from 'hooks/useIsViewed';
import {autorun} from 'mobx';
import ChatModuleInputs from './ChatModuleInputs';
import {LoadingIndicator} from '../shared/LoadingIndicator';
import {hasReadReceipt} from '../../mobx/PubnubStore';

const PfChatModule = (props) => {
  const {meStore, memberStore, pubnubStore, channelStore, tutorialStore} =
    useStores();

  const chatStream = useRef();
  const {ref: chatStreamInViewRef, isViewed: isChatViewed} = useIsViewed();
  const markedRef = useRef('');
  const [channel, setChannel] = useState(null);
  const [chatMember, setChatMember] = useState(null);
  const channelRef = useRef(channel);
  const [isHistoryEmpty, setIsHistoryEmpty] = useState(true);
  const [userChannelAccess, setUserChannelAccess] = useState({denied: false});
  const [loading, setLoading] = useState(false);
  const [timetoken, setTimetoken] = useState(null);
  const historyChunk = 5;

  // One ref for both scrolling and the in-view observer. useCallback keeps it
  // stable - a new function each render would restart the observer.
  const setChatStreamRef = useCallback(
    (node) => {
      chatStream.current = node;
      chatStreamInViewRef(node);
    },
    [chatStreamInViewRef],
  );

  const scrollStream = (direction) => {
    if (chatStream.current) {
      if (direction === 'up') {
        chatStream.current.scrollTop = 0;
      } else {
        scrollTo(chatStream.current, chatStream.current.scrollHeight, 500);
      }
    }
  };

  useEffect(() => {
    if (channel) {
      const [user] = channel.members;

      if (user) {
        memberStore.getMemberForUserId(user.id).then((member) => {
          setChatMember(member);
        });
      }
    }
  }, [channel, memberStore]);

  useEffect(
    () =>
      autorun(() => {
        if (timetoken && pubnubStore.firstMessage) {
          scrollStream('up');
        }
      }),

    [pubnubStore.firstMessage],
  );

  useEffect(
    () =>
      autorun(() => {
        if (pubnubStore.lastMessage) {
          scrollStream('down');
        }
      }),
    [pubnubStore.lastMessage],
  );

  const closeChannel = useCallback(
    (channelId) => {
      pubnubStore.clearMessages();
      if (channelId) {
        pubnubStore.unsubscribeFromChannelPresence(['ch-chat.' + channelId]);
      }
    },
    [pubnubStore],
  );

  // componentDidMount
  useEffect(() => {
    return () => {
      closeChannel(channelRef.current?.id);
    };
  }, []);

  //If the props.channel or props.user changes then close the current channel and set the new channel
  //This triggers the next useEffect to load the channel
  useEffect(() => {
    async function fetchChannel() {
      //close the current channel
      closeChannel(channelRef.current?.id);
      //Channels are either loaded by a known channel.id OR a known user.id
      if (props.channel) {
        // Extract the channelId If channel is a Pubnub Channel Name: 'ch-chat.<channelId>'
        let channelId =
          props.channel.indexOf('.') > 0
            ? props.channel.split('.')[1]
            : props.channel;
        setChannel(await channelStore.getChannelById(channelId));
      } else if (props.user) {
        setChannel(await channelStore.getChannelByUser(props.user));
      }
    }
    fetchChannel();
  }, [props.channel, props.user, channelStore, closeChannel]);

  //When the channel changes we need to load the new channel
  useEffect(() => {
    if (!channel) {
      return;
    }

    const loadChannel = async () => {
      // refresh the token with the "active" channel
      await pubnubStore.setTokenForChannel(channel?.name);
      channelRef.current = channel;
      setUserChannelAccess({denied: false});
      setLoading(true);
      pubnubStore.isPartnerTyping = false;
      // TODO this is breaking channels currently. Revisit when chat is more functional
      // pubnubStore.subscribeToChannelPresence([
      //   'ch-chat.' + channel.id + '-pnpres',
      // ]);

      let historyObj = {
        channels: [channel?.name],
        count: historyChunk,
      };
      pubnubStore.setActive(channel);
      pubnubStore.getChannelHistory(
        historyObj,
        (msgs, {hasError, hasMoreMessages, status}) => {
          setLoading(false);
          setIsHistoryEmpty(!hasMoreMessages);
          if (hasError) {
            let errorMessage;
            if (status.statusCode === 403) {
              errorMessage =
                'You do not have access to this conversation. Please contact Pathfinder if this is an error.';
            } else {
              errorMessage =
                'Error loading chat history, please try again. If the error continues, contact Pathfinder.';
            }

            setUserChannelAccess({
              denied: true,
              message: errorMessage,
            });
          } else {
            const tt = pubnubStore.messages.length
              ? pubnubStore.messages[0].timetoken
              : getPubnubTimetokenFromTimestamp(1);
            setTimetoken(tt);
            scrollStream('down');
          }
        },
      );
    };

    loadChannel();
  }, [channel, channelStore, meStore.me.id, pubnubStore]);

  const lastMessageTimetoken = pubnubStore.messages.length
    ? pubnubStore.messages[pubnubStore.messages.length - 1].timetoken
    : null;
  const isViewingChannel =
    !!channel && pubnubStore.activeChannel?.name === channel.name;

  // Tell the store whether this chat is being looked at, so messages that
  // arrive while it's open but unwatched still count as unread.
  useEffect(() => {
    pubnubStore.setActiveChannelViewed(isChatViewed && isViewingChannel);
    return () => pubnubStore.setActiveChannelViewed(false);
  }, [isChatViewed, isViewingChannel, pubnubStore]);

  // Mark the conversation read (receipt to the member, unread cleared for us),
  // but only while the chat is being looked at. Re-runs when isChatViewed
  // turns true, which catches up messages that arrived while we were away.
  useEffect(() => {
    if (!isChatViewed || !isViewingChannel || !lastMessageTimetoken) {
      return;
    }

    // The store skips receipts it has already sent, so this is safe to repeat,
    // and it retries one that failed.
    const messages = pubnubStore.messages;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      const author = m?.message?.data?.author;
      if (author && author !== meStore.me.id && m.timetoken) {
        const alreadyRead = m.actions?.receipt?.read?.some(
          (receipt) => receipt.uuid === meStore.me.id,
        );
        if (!alreadyRead) {
          pubnubStore.sendReadReceipt(channel.name, m.timetoken);
        }
        break;
      }
    }

    // Save last_viewed once per newest message. If the save fails, forget it so
    // the next look retries.
    const markKey = `${channel.id}:${lastMessageTimetoken}`;
    if (markedRef.current === markKey) {
      return;
    }
    markedRef.current = markKey;

    channelStore.clearUnreadChannel(channel);
    channelStore
      .updateChannelData(channel, {
        last_viewed: {
          [meStore.me.id]: new Date(),
        },
      })
      .then((response) => {
        // The tutorial skips the save on purpose, so don't retry it
        if (
          !response?.isSuccess &&
          !tutorialStore.isActive &&
          markedRef.current === markKey
        ) {
          markedRef.current = '';
        }
      });
  }, [
    channel,
    channelStore,
    isChatViewed,
    isViewingChannel,
    lastMessageTimetoken,
    meStore,
    pubnubStore,
    tutorialStore,
  ]);

  const createChannel = async () => {
    const params = {
      members: [{id: props.user}],
    };

    try {
      setLoading(true);
      const response = await Api.Instance.current().post(
        '/messaging/channels',
        params,
      );
      // Refresh the pubnubToken for the new channel
      await pubnubStore.setPubnubToken();
      setChannel(response.data);
      setUserChannelAccess({denied: false});
      setLoading(false);
    } catch (e) {
      setUserChannelAccess({
        denied: true,
        message:
          'You may not have access to chat with this member. Please reach out to an Admin if you need to get in contact.',
      });
      setLoading(false);
    }
  };

  const loadMoreHistory = (notFound, found) => {
    let historyObj = {
      channels: [channel?.name],
      count: historyChunk,
    };

    if (timetoken) {
      historyObj.start = timetoken;
    }

    pubnubStore.getChannelHistory(historyObj, (msgs, {hasMoreMessages}) => {
      const tt = pubnubStore.messages[0]
        ? pubnubStore.messages[0].timetoken
        : getPubnubTimetokenFromTimestamp(1);
      setTimetoken(tt);
      setIsHistoryEmpty(!hasMoreMessages);
    });
  };

  if (loading) {
    return (
      <div style={styles.noChatBox}>
        <LoadingIndicator />
      </div>
    );
  }
  if (userChannelAccess.denied) {
    return (
      <Alert severity="info">
        <p>{userChannelAccess.message}</p>
      </Alert>
    );
  } else if (!channel && !tutorialStore.isActive) {
    return (
      <div style={styles.noChatBox}>
        <Button variant="contained" onClick={createChannel}>
          Start Conversation
        </Button>
      </div>
    );
  }
  return (
    <div>
      <Paper style={styles.chatBox} sx={{padding: 3, marginBottom: 2}}>
        <div style={styles.chatFrame}>
          <div ref={setChatStreamRef} style={styles.chatStream}>
            {!isHistoryEmpty && (
              <div
                onClick={() => loadMoreHistory()}
                style={styles.moreHistoryButton}>
                view more
              </div>
            )}
            {(() => {
              const messages = pubnubStore.messages;
              let lastReadIdx = -1;
              for (let i = messages.length - 1; i >= 0; i--) {
                const m = messages[i];
                if (
                  m.message.data.author === meStore.me.id &&
                  hasReadReceipt(m)
                ) {
                  lastReadIdx = i;
                  break;
                }
              }
              return messages.map((m, index) => {
                const owner =
                  m.message.data.author === meStore.me.id ? 'me' : 'other';
                return (
                  <PfMessage
                    message={m}
                    owner={owner}
                    showReadReceipt={index === lastReadIdx}
                    key={'message' + index}
                  />
                );
              });
            })()}
            <div style={hiddenIfEmpty(pubnubStore.isPartnerTyping)}>
              <div style={styles.typingIndicator}>Typing...</div>
            </div>
          </div>
        </div>
      </Paper>
      <ChatModuleInputs channel={channel} chatMember={chatMember} />
    </div>
  );
};

const styles = {
  noChatBox: {
    height: 440,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  chatBox: {
    height: 440,
    position: 'relative',
  },
  chatFrame: {
    height: 400,
    overflowY: 'hidden',
  },
  chatStream: {
    height: 400,
    width: '100%',
    overflowY: 'auto',
    paddingRight: 10,
  },
  moreHistoryButton: {
    cursor: 'pointer',
    width: '100%',
    textAlign: 'center',
    color: Palette.LINK,
  },
  typingIndicator: {
    color: Palette.INFO,
    borderRadius: 10,
    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 10,
    paddingRight: 10,
  },
};

export default observer(PfChatModule);
